import {
  archivedDepartmentsQuery,
  departmentsQuery,
} from "@common/api/departments"
import type { DepartmentPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { GridTable } from "@common/components/ui/grid-table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { Network } from "lucide-react"
import { z } from "zod"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"

const COLUMNS = "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)]"

const filterSchema = z.object({
  archived: z.boolean().optional().catch(false),
  search: z.string().optional().catch(""),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/departments/_list",
)({
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({ archived: search.archived }),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(
      deps.archived ? archivedDepartmentsQuery() : departmentsQuery(),
    ),
  component: DepartmentsPage,
})

function DepartmentsPage(): React.ReactElement {
  const { search = "" } = Route.useSearch()

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar search={search} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <DepartmentResults search={search} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({ search }: { search: string }): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { archived = false } = Route.useSearch()
  const canCreate = useHasPermission("department:create")
  const filter = (next: string) =>
    navigate({
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) =>
            navigate({
              search: (previous) => ({
                ...previous,
                archived: tab === "archived" || undefined,
              }),
              replace: true,
            })
          }
        />
      }
      actions={
        <>
          <NameFilter search={search} onSearch={filter} />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/workspace/departments/create">
                New department
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function DepartmentResults({ search }: { search: string }): React.ReactElement {
  const { archived = false } = Route.useSearch()
  const { data } = useSuspenseQuery(
    archived ? archivedDepartmentsQuery() : departmentsQuery(),
  )
  const params = useParams({ strict: false })
  const departmentId =
    "departmentId" in params ? params.departmentId : undefined
  const needle = search.toLowerCase()
  const departments = data.data.filter((department) =>
    department.name.toLowerCase().includes(needle),
  )

  if (departments.length === 0) {
    return <EmptyState search={search} archived={archived} />
  }

  return (
    <GridTable className={COLUMNS}>
      <SettingsTableHeader>
        <SettingsTableHead className="pl-6">Name</SettingsTableHead>
        <SettingsTableHead>Phone</SettingsTableHead>
        <SettingsTableHead className="pr-6">Email</SettingsTableHead>
      </SettingsTableHeader>
      {departments.map((department) => (
        <DepartmentRow
          key={department.id}
          department={department}
          selected={department.id === departmentId}
        />
      ))}
    </GridTable>
  )
}

function DepartmentRow({
  department,
  selected,
}: {
  department: DepartmentPublic
  selected: boolean
}): React.ReactElement {
  return (
    <SettingsTableRow
      asChild
      archived={Boolean(department.deleted_at)}
      aria-selected={selected}
      selected={selected}
    >
      <Link
        to="/settings/workspace/departments/$departmentId"
        params={{ departmentId: department.id }}
        search={true}
      >
        <SettingsTableCell className="pl-6">
          <span className="truncate">{department.name}</span>
        </SettingsTableCell>
        <SettingsTableCell className="text-small-regular text-muted-foreground">
          <span className="truncate">{department.phone}</span>
        </SettingsTableCell>
        <SettingsTableCell className="pr-6 text-muted-foreground">
          <span className="truncate">{department.email}</span>
        </SettingsTableCell>
      </Link>
    </SettingsTableRow>
  )
}

function EmptyState({
  search,
  archived,
}: {
  search: string
  archived: boolean
}): React.ReactElement {
  const title = search
    ? "No matching departments"
    : archived
      ? "No archived departments"
      : "No departments"
  const body = search
    ? "Try a different name."
    : archived
      ? "Archived departments appear here."
      : "Add the shops that own your work, like avionics or airframe."

  return <ListEmpty icon={Network} title={title} body={body} />
}
